import React, { useCallback } from 'react';

import { useFocusEffect, useLocalSearchParams, usePathname } from 'expo-router';
import { useIntl } from 'react-intl';

import { CenteredRowContainer } from 'components/layout/centered-row-container';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import { useAuthContext } from 'state/auth';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { routes } from 'utils/routing';
import { parseBool } from 'utils/string-To-Bool';

import {
  RewardsTabLink,
  RewardsTabLinkText,
  RewardsTabsBorderBottom,
} from './loyalty-rewards.styled';

// TODO switch to https://github.com/EvanBacon/expo-router-top-tabs
export const LoyaltyRewardsTabs: React.FC = () => {
  const { formatMessage } = useIntl();
  const pathname = usePathname();
  const { showSignUp: _showSignUp = true } = useLocalSearchParams<{ showSignUp: string }>();
  const showSignUp = parseBool(_showSignUp);

  const { loyaltyUser } = useLoyaltyUser();

  const redeemTabKey = 'redeemTab';
  const historyTabKey = 'historyTab';

  const isIncentiveSelected = pathname && !!pathname.split('/')[3];
  const showCtaBar = !isIncentiveSelected;
  const showTabs = !!showCtaBar;

  const redeemTab = {
    tabItemId: redeemTabKey,
    label: formatMessage({ id: redeemTabKey }),
    to: routes.rewardsList,
  };

  const historyTab = {
    tabItemId: historyTabKey,
    label: formatMessage({ id: historyTabKey }),
    to: routes.rewardsHistory,
  };

  const rewardsTabs = [redeemTab, historyTab];

  const tabWidth = `${100 / rewardsTabs.length}%`;
  const { isAuthenticated, loading: isLoadingAuth, modalAuthIsOpen } = useAuthContext();
  const { closeModalAuth, openSignInModal } = useAuthContext();

  useFocusEffect(
    useCallback(() => {
      if (!isAuthenticated && !isLoadingAuth && showSignUp && !modalAuthIsOpen) {
        openSignInModal({
          ...(pathname.includes(routes.loyaltyActivateQuest)
            ? {
                navigateOnSuccessRoute: routes.loyaltyActivateQuest,
              }
            : {}),
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isLoadingAuth, showSignUp, openSignInModal, pathname])
  );

  useEffectOnUnmount(() => {
    closeModalAuth();
  });

  if (!showTabs) {
    return false;
  }

  return (
    <CenteredRowContainer testID="rewards-tabs" contentFlexDirection="row">
      {rewardsTabs.map(({ label, to }) => {
        const isActiveTab = pathname === to;
        return (
          <RewardsTabLink
            replace
            key={label}
            linkPath={!loyaltyUser ? `${to}?showSignUp=false` : to}
            testID={`tabbed-reward-${label}`}
            active={isActiveTab}
            minWidth={tabWidth}
          >
            <RewardsTabLinkText variant="headerThree" active={isActiveTab}>
              {label}
            </RewardsTabLinkText>
          </RewardsTabLink>
        );
      })}
      <RewardsTabsBorderBottom />
    </CenteredRowContainer>
  );
};
